import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
} from "@heroicons/react/20/solid";
import type { AuditScoreType } from "@ots-frontend/ots-data";
import type { ReactNode, Ref } from "react";
import { OtsIconActionButton } from "../components/OtsButton";
import { OtsSearchInput } from "../components/OtsSearchInput";
import { OtsDayStepper } from "../components/OtsDayStepper";
import { OtsPopover } from "../components/OtsPopover";
import { OtsSegmentedControl } from "../components/OtsSegmentedControl";
import { OtsSwitch } from "../components/OtsSwitch";
import { ActivityRateModelChoices } from "./ActivityRateModelChoices";
import type { ActivityDurationMode, ActivityRateModelOption, ActivityStats, ActivityViewFilter, ActivityIdLabel } from "./driver-activity-view-model";

/** What the workspace shows and highlights; safe to persist per user. */
export type DriverActivityDisplayOptions = {
  showVisits: boolean;
  showDispatches: boolean;
  showSiteWork: boolean;
  showTankDrops: boolean;
  markScada: boolean;
  markDemurrage: boolean;
  /** Each driver's day as a strip in their header. */
  showTimeline: boolean;
  compactRows: boolean;
  durationMode: ActivityDurationMode;
  /** Which confidence the audit rings show. */
  auditScoreType: AuditScoreType;
  /** What the ID column shows: the resource ID, or the paper ticket number when a ticket has one. */
  idLabel: ActivityIdLabel;
  /** Rate models counted towards revenue and cost; the hauling ones when missing. */
  rateModels?: string[];
};

export const DEFAULT_DRIVER_ACTIVITY_DISPLAY: DriverActivityDisplayOptions = {
  showVisits: true,
  showDispatches: true,
  showSiteWork: true,
  showTankDrops: true,
  markScada: true,
  markDemurrage: false,
  showTimeline: true,
  compactRows: false,
  durationMode: "turnaround",
  auditScoreType: "BUSINESS_VALIDATION_CONFIDENCE",
  idLabel: "resourceId",
};

export type DriverActivityToolbarProps = {
  date: Date;
  onDateChange?: (date: Date) => void;
  today?: Date;
  query: string;
  onQueryChange: (query: string) => void;
  /** The search box, so a host can focus it from a shortcut. */
  searchRef?: Ref<HTMLInputElement>;
  view: ActivityViewFilter;
  onViewChange: (view: ActivityViewFilter) => void;
  /** Unfiltered day counts, shown on the view switch. */
  stats?: Pick<ActivityStats, "loads" | "attention" | "unmatched" | "unvalidated">;
  display: DriverActivityDisplayOptions;
  onDisplayChange: (display: DriverActivityDisplayOptions) => void;
  allCollapsed?: boolean;
  onToggleAll?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** When the rows were last loaded; shown as "Updated 2 min ago". */
  lastUpdated?: number;
  now?: number;
  onExport?: () => void;
  /** Offers the audit confidence choice; on when the host has loaded audits. */
  showAuditOptions?: boolean;
  /** The rate models on offer (the region's and the day's); revenue and cost come from some of them. */
  rateModelOptions?: ActivityRateModelOption[];
  /** The rate models counted now: those picked, else the hauling ones. */
  countedRateModels?: string[];
  /** Shows the All / Needs review / Unmatched / Unvalidated quick views. Off for now. */
  showViewFilter?: boolean;
  /** Leaves the day stepper out, for a host that places it itself (the workspace centers it in its header). */
  hideDayStepper?: boolean;
  /** Shown before the search, such as the New menu. */
  leading?: ReactNode;
  /** Shown after the search, such as the Filters popover. */
  filters?: ReactNode;
  className?: string;
};

type Toggle = Exclude<keyof DriverActivityDisplayOptions, "durationMode" | "auditScoreType" | "rateModels" | "idLabel">;

const toggles: { title: string; options: [Toggle, string, string][] }[] = [
  {
    title: "Rows",
    options: [
      ["showVisits", "Site visits", "Geofence visits with no ticket"],
      ["showDispatches", "Dispatches", "Work assigned to the driver"],
      ["showSiteWork", "Site work", "Jobs done at a site between tickets"],
      ["showTankDrops", "Tank drops", "Level drops with no ticket against them"],
    ],
  },
  {
    title: "Highlights",
    options: [
      ["markScada", "SCADA comparison", "Color pickup quantities against SCADA"],
      ["markDemurrage", "Demurrage", "Flag revenue that includes demurrage"],
    ],
  },
  {
    title: "Layout",
    options: [
      ["showTimeline", "Day timelines", "Each driver's day as a strip"],
      ["compactRows", "Compact rows", "Fit more on screen"],
    ],
  },
];

const formatAge = (millis: number) => {
  const minutes = Math.floor(millis / 60_000);
  if (minutes < 1) return "just now";
  return minutes < 60 ? `${minutes} min ago` : `${Math.floor(minutes / 60)} h ago`;
};

const heading = "text-[10px] font-bold uppercase tracking-wide text-[var(--ots-text-muted)]";

/** Day, search, quick views, freshness, export, and display options for the driver activity workspace. */
export const DriverActivityToolbar = ({
  date,
  onDateChange,
  today,
  query,
  onQueryChange,
  searchRef,
  view,
  onViewChange,
  stats,
  display,
  onDisplayChange,
  allCollapsed = false,
  onToggleAll,
  onRefresh,
  isRefreshing = false,
  lastUpdated,
  now = Date.now(),
  onExport,
  showAuditOptions = false,
  rateModelOptions = [],
  countedRateModels,
  showViewFilter = false,
  hideDayStepper = false,
  leading,
  filters,
  className,
}: DriverActivityToolbarProps) => (
  <div className={["flex flex-wrap items-center gap-x-3 gap-y-2", className].filter(Boolean).join(" ")}>
    {!hideDayStepper && <OtsDayStepper size="compact" value={date} today={today} disabled={!onDateChange} onChange={(next) => onDateChange?.(next)}/>}
    {leading}
    {showViewFilter && (
      <OtsSegmentedControl<ActivityViewFilter>
        size="compact"
        ariaLabel="Show"
        value={view}
        onChange={onViewChange}
        segments={[
          { id: "all", label: "All", count: stats?.loads },
          { id: "attention", label: "Needs review", count: stats?.attention },
          { id: "unmatched", label: "Unmatched", count: stats?.unmatched },
          { id: "unvalidated", label: "Unvalidated", count: stats?.unvalidated },
        ]}/>
    )}
    <div className="ml-auto flex items-center gap-1">
      {lastUpdated != null && (
        <span role="status" className="mr-1 flex items-center gap-1.5 whitespace-nowrap text-xs text-[var(--ots-text-muted)]">
          Updated {formatAge(Math.max(now - lastUpdated, 0))}
        </span>
      )}
      <div className="w-64">
        <OtsSearchInput
          ref={searchRef}
          label="Search driver activity"
          hideLabel
          size="compact"
          aria-keyshortcuts="/"
          placeholder="Search drivers, tickets, sites…  ( / )"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onClear={() => onQueryChange("")}/>
      </div>
      {filters}
      <OtsPopover
        density="compact"
        placement="bottom end"
        ariaLabel="Display options"
        tooltip="Display options"
        buttonClassName="inline-flex size-8 items-center justify-center rounded-lg text-[var(--ots-primary)] outline-none hover:bg-[var(--ots-primary-container)] focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]"
        panelClassName="grid w-[36rem] !max-w-[calc(100vw-2rem)] grid-cols-1 gap-x-6 gap-y-3 !p-3 sm:grid-cols-2"
        trigger={<AdjustmentsHorizontalIcon aria-hidden="true" className="size-[18px]"/>}>
        {/* Two columns: what to show on the left, how to show it on the right. */}
        <div data-display-column="switches" className="flex flex-col gap-3">
        {toggles.map((group) => (
          <fieldset key={group.title} className="flex flex-col gap-2">
            <legend className={[heading, "mb-1.5"].join(" ")}>{group.title}</legend>
            {group.options.map(([key, label, description]) => (
              <OtsSwitch
                key={key}
                size="compact"
                label={label}
                description={description}
                checked={display[key]}
                onCheckedChange={(checked) => onDisplayChange({ ...display, [key]: checked })}/>
            ))}
          </fieldset>
        ))}
        </div>
        <div data-display-column="choices" className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <span className={heading}>ID column</span>
          <OtsSegmentedControl<ActivityIdLabel>
            size="compact"
            ariaLabel="ID column"
            value={display.idLabel ?? "resourceId"}
            onChange={(idLabel) => onDisplayChange({ ...display, idLabel })}
            segments={[
              { id: "resourceId", label: "Resource ID" },
              { id: "ticketNumber", label: "Paper ticket #" },
            ]}/>
          <span className="text-[11px] text-[var(--ots-text-muted)]">Paper ticket # falls back to the resource ID when a ticket has none.</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={heading}>Duration column</span>
          <OtsSegmentedControl<ActivityDurationMode>
            size="compact"
            ariaLabel="Duration column"
            value={display.durationMode}
            onChange={(durationMode) => onDisplayChange({ ...display, durationMode })}
            segments={[
              { id: "turnaround", label: "Turnaround" },
              { id: "onSite", label: "On site" },
              { id: "travel", label: "Travel" },
              { id: "billable", label: "Billable" },
            ]}/>
        </div>
        {showAuditOptions && (
          <div className="flex flex-col gap-1.5">
            <span className={heading}>Audit confidence</span>
            <OtsSegmentedControl<AuditScoreType>
              size="compact"
              ariaLabel="Audit confidence"
              value={display.auditScoreType}
              onChange={(auditScoreType) => onDisplayChange({ ...display, auditScoreType })}
              segments={[
                { id: "BUSINESS_VALIDATION_CONFIDENCE", label: "Business" },
                { id: "FIELD_VALIDATION_CONFIDENCE", label: "Field" },
              ]}/>
          </div>
        )}
        {rateModelOptions.length > 0 && (
          <ActivityRateModelChoices
            options={rateModelOptions}
            counted={countedRateModels ?? rateModelOptions.map(({ name }) => name)}
            value={display.rateModels}
            onChange={(rateModels) => onDisplayChange({ ...display, rateModels })}
            headingClassName={[heading, "mb-1.5"].join(" ")}/>
        )}
        </div>
      </OtsPopover>
      {onToggleAll && (
        <OtsIconActionButton
          variant="text"
          size="compact"
          icon={allCollapsed ? <ChevronDoubleDownIcon/> : <ChevronDoubleUpIcon/>}
          label={allCollapsed ? "Expand all drivers" : "Collapse all drivers"}
          onClick={onToggleAll}/>
      )}
      {onExport && <OtsIconActionButton variant="text" size="compact" icon={<ArrowDownTrayIcon/>} label="Export to CSV" tooltip="Export what is shown to CSV" onClick={onExport}/>}
      {onRefresh && (
        <OtsIconActionButton
          variant="text"
          size="compact"
          disabled={isRefreshing}
          icon={<ArrowPathIcon className={isRefreshing ? "animate-spin" : undefined}/>}
          label="Refresh"
          onClick={onRefresh}/>
      )}
    </div>
  </div>
);
